/**
 * Simple audio manager stub.
 * Will be expanded with actual sound effects later.
 */
export class Audio {
  private sfxVolume = 1.0;
  private musicVolume = 0.7;
  private muted = false;

  // Audio context (lazy init to comply with autoplay policies)
  private ctx: AudioContext | null = null;

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    return this.ctx;
  }

  /** Resume audio context (call from user gesture) */
  async resume(): Promise<void> {
    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, v));
  }

  setMusicVolume(v: number): void {
    this.musicVolume = Math.max(0, Math.min(1, v));
  }

  setMuted(m: boolean): void {
    this.muted = m;
  }

  // Placeholder sound methods - to be implemented with actual assets

  playFlipperUp(): void {
    if (this.muted) return;
    this.playTone(200, 0.05, 0.3);
  }

  playFlipperDown(): void {
    if (this.muted) return;
    this.playTone(150, 0.03, 0.2);
  }

  playTargetHit(): void {
    if (this.muted) return;
    this.playTone(440, 0.1, 0.4);
  }

  playGateOpen(): void {
    if (this.muted) return;
    // Rising arpeggio
    this.playTone(330, 0.1, 0.3);
    setTimeout(() => this.playTone(440, 0.1, 0.3), 100);
    setTimeout(() => this.playTone(550, 0.15, 0.4), 200);
  }

  playFinish(): void {
    if (this.muted) return;
    // Victory jingle
    this.playTone(440, 0.15, 0.5);
    setTimeout(() => this.playTone(550, 0.15, 0.5), 150);
    setTimeout(() => this.playTone(660, 0.3, 0.6), 300);
  }

  playFall(): void {
    if (this.muted) return;
    // Descending tone
    this.playTone(300, 0.2, 0.3, -0.5);
  }

  /** Simple oscillator-based tone for placeholder sounds */
  private playTone(
    freq: number,
    duration: number,
    volume: number,
    pitchBend = 0
  ): void {
    try {
      const ctx = this.getContext();
      if (ctx.state !== 'running') return;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      if (pitchBend !== 0) {
        osc.frequency.linearRampToValueAtTime(
          freq * (1 + pitchBend),
          ctx.currentTime + duration
        );
      }

      gain.gain.setValueAtTime(volume * this.sfxVolume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + duration + 0.1);
    } catch {
      // Audio not available
    }
  }

  dispose(): void {
    this.ctx?.close();
    this.ctx = null;
  }
}
